import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useOrganization } from "../../../shared/store/organizationStore";
import { useIframeToken } from "../../hooks/useIframeToken";
import { buildFormCompletionURL } from "../../../config/portal";

/**
 * Legacy form link page that handles direct form submission URLs.
 * Maps to old portal's /link/form route.
 *
 * Expected query params:
 * - f: formId
 * - a: accessCode
 * - token: authToken (processed by EnduserRouteHandler)
 * - stepId: automationStepId (optional)
 * - startAtNext: whether to start at next unanswered question (optional)
 * - hideBg: hide background (optional)
 */
export const FormLinkPage = () => {
  const { t } = useTranslation(["forms", "common"]);
  const [searchParams] = useSearchParams();
  const organization = useOrganization();

  // Extract query params
  const formId = searchParams.get("f") ?? "";
  const accessCode = searchParams.get("a") ?? "";
  const automationStepId = searchParams.get("stepId") ?? undefined;
  const startAtNext = searchParams.get("startAtNext") === "true";
  const hideBg = searchParams.get("hideBg") === "true";

  // Validate required params
  const hasRequiredParams = !!(formId && accessCode);

  // Fetch iframe token using TanStack Query hook
  const { token: iframeToken, isLoading, error: tokenError } = useIframeToken({ enabled: hasRequiredParams });

  // Determine error message (missing params or token fetch error)
  const error = !hasRequiredParams
    ? t("Missing required form parameters", { ns: "common" })
    : tokenError
    ? tokenError instanceof Error
      ? tokenError.message
      : t("Failed to authenticate", { ns: "common" })
    : null;

  // Build iframe URL
  const iframeUrl =
    organization && iframeToken && hasRequiredParams
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId,
          accessCode,
          authToken: iframeToken,
          automationStepId,
          startAtNext,
          hideBg,
        })
      : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading form...")}</p>
        </div>
      </div>
    );
  }

  if (error || !iframeUrl) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-destructive">
          <p className="text-sm font-medium mb-1">{t("Unable to load form")}</p>
          <p className="text-xs text-muted-foreground">{error ?? t("Missing required parameters")}</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={iframeUrl}
      className="w-full h-screen border-0"
      title={t("Form Submission")}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};
